import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { getDashboardData, getMonthlyLoadSummary } from 'apis/adminServices/dashboardService';
import { useGlobalLoader } from 'contexts/GlobalLoaderContext';
import { Link } from 'react-router-dom';
import { useUserStore } from 'store/userStore';



const AdminDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [monthlyData, setMonthlyData] = useState<any>([]);
  const { showLoader, hideLoader } = useGlobalLoader();
  const { username } = useUserStore(state => state);

  useEffect(() => {
    showLoader();
    const fetchData = async () => {
      try {
        const data = await getDashboardData();
        setDashboardData(data);
        showLoader();
        const currentYear = new Date().getFullYear(); // Get current year dynamically
        const monthlyLoadData = await getMonthlyLoadSummary(currentYear); // Pass year here

        const formattedData = monthlyLoadData.map((item: any) => ({
          month: item.month.substring(0, 3), // Shorten month name to 3 characters
          Posted: item.loadsPosted,
          Answered: item.loadsAnswered
        }));

        setMonthlyData(formattedData);
      } catch (err) {
        console.error('Error loading dashboard data', err);
      } finally {
        hideLoader();
      }
    };

    fetchData();
  }, []);

  // Pie chart data
  const loadDistributionData = [
    { name: 'Posted', value: dashboardData?.loadPostedCount || 0 },
    { name: 'Answered', value: dashboardData?.loadAnsweredCount || 0 }
  ];

  const renderCustomizedLabel = (props: any) => {
    const RADIAN = Math.PI / 180;
    const { cx, cy, midAngle, outerRadius, fill, payload, index, startAngle, endAngle } = props;

    // Calculate the position for the external label
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);

    // Adjust these values to position the labels correctly
    const labelRadius = outerRadius * 1.2;
    const x = cx + labelRadius * cos;
    const y = cy + labelRadius * sin;

    // Position for the connector dot - start further inside the pie
    const dotX = cx + outerRadius * 0.7 * cos;
    const dotY = cy + outerRadius * 0.7 * sin;

    // Determine box position based on which half of the pie
    const isRightSide = cos > 0;

    // Adjust box position based on label content
    let boxX = isRightSide ? x : x - 110;

    // Adjust position based on label content
    if (payload.name === "Posted") {
      boxX += 15; // Move "Posted" label a bit to the right
    } else if (payload.name === "Answered") {
      boxX -= 15; // Move "Answered" label a bit to the left
    }

    return (
      <g>
        {/* Connector line */}
        <line
          x1={dotX}
          y1={dotY}
          x2={x}
          y2={y}
          stroke="#000"
          strokeWidth={1}
        />

        {/* Connector dot */}
        <circle
          cx={dotX}
          cy={dotY}
          r={4}
          fill="#000"
        />

        {/* Label box with content inside */}
        <foreignObject
          x={boxX}
          y={y - 25}
          width={110}
          height={50}
        >
          <div
            style={{
              backgroundColor: '#1A1C31',
              color: 'white',
              borderRadius: '8px',
              padding: '8px 12px',
              fontSize: '14px',
              fontWeight: 'bold',
              textAlign: 'center',
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              boxSizing: 'border-box'
            }}
          >
            <div style={{
              fontSize: '12px',
              fontWeight: '100', opacity: '0.85'
            }}>{payload.name}</div>
            <div style={{ fontSize: '16px', fontWeight: '600' }}>
              {payload.value.toLocaleString()}
            </div>
          </div>
        </foreignObject>
      </g>
    );
  };


  return (
    <div className="bg-offwhite-100 font-poppins">
      <div className="px-6 pt-6">
        <section className="bg-white p-4 rounded-xl mt-6 flex items-center gap-4">
          <img className="h-14 w-14" src="/images/avatar.png" alt="" />
          <div>
            <p className="text-primaryblue-100 text-xs">Welcome Back!</p>
            <p className="mt-1 text-primaryblue-100 text-xl font-bold">
              {username ? `${username.charAt(0).toUpperCase()}${username.slice(1)}` : 'UserName'}
            </p>

          </div>
        </section>

        <section className="grid md:grid-cols-12 gap-6 mt-6">
          <div className="col-span-8 grid sm:grid-cols-2 gap-6">
            <Link to="/admin/load-posted">
              <div className="bg-white p-4 rounded-xl flex items-center justify-between gap-4">
                <div className="flex gap-4 items-center">
                  <div className="flex items-center justify-center h-10 w-10 rounded-full bg-violet-100">
                    <img className="w-5 h-5" src="/images/seat.svg" alt="" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-light tracking-wide">Total Loads Posted</p>
                    <p className="mt-1 text-grease-100 text-xl font-bold">{dashboardData?.loadPostedCount}</p>
                  </div>
                </div>
                <i className="st-arrow-left rotate-180 text-xs text-royalblue-100"></i>
              </div>
            </Link>
            <Link to="/admin/load-answered">
              <div className="bg-white p-4 rounded-xl flex items-center justify-between gap-4">
                <div className="flex gap-4 items-center">
                  <div className="flex items-center justify-center h-10 w-10 rounded-full bg-skyblue-100">
                    <img className="w-5 h-5" src="/images/seat.svg" alt="" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-light tracking-wide">Total Loads Answered</p>
                    <p className="mt-1 text-grease-100 text-xl font-bold">{dashboardData?.loadAnsweredCount}</p>
                  </div>
                </div>
                <i className="st-arrow-left rotate-180 text-xs text-royalblue-100"></i>
              </div>
            </Link>
            <div className="bg-white rounded-xl col-span-2 p-4">
              <div className="flex items-center justify-between gap-4 mb-4">
                <p className="text-sm text-grease-100 font-semibold">Loads Trend</p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div className="bg-violet-100 h-3 w-3 rounded-full"></div>
                    <p className="text-gray-500 text-xs">Posted</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="bg-skyblue-100 h-3 w-3 rounded-full"></div>
                    <p className="text-gray-500 text-xs">Answered</p>
                  </div>
                </div>
                <form>
                  <select
                    id="underline_select"
                    className="block py-2.5 pl-2 pr-8 w-full text-xs rounded-lg text-gray-500 bg-transparent border-none peer focus:outline-none cursor-pointer"
                  >
                    <option defaultValue="This Year">This Year</option>
                  </select>
                </form>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyData} barGap={8}>
                  <CartesianGrid
                    strokeDasharray="0"
                    horizontal={true}
                    vertical={false}
                    stroke="#F0F0F0"
                  />
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#A0AEC0', fontSize: 12 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#A0AEC0', fontSize: 12 }}
                  />
                  <Tooltip />
                  <Bar
                    dataKey="Posted"
                    fill="rgb(95 112 228 / 1)"
                    radius={[10, 10, 10, 10]}
                    barSize={14}
                  />
                  <Bar
                    dataKey="Answered"
                    fill="rgb(95 210 228 / 1)"
                    radius={[10, 10, 10, 10]}
                    barSize={14}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="col-span-4 bg-white rounded-xl p-3">
            <div className="flex items-center justify-between mb-16">
              <p className="text-sm text-grease-100 font-semibold">Load Distribution</p>
            </div>
            <div className="flex justify-center items-center">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                  <Pie
                    data={loadDistributionData}
                    cx="50%"
                    cy="50%"
                    outerRadius={140}
                    fill="#8884d8"
                    dataKey="value"
                    animationBegin={0}
                    animationDuration={500} // Reduce animation duration for smoother experience
                    animationEasing="ease-out" // Use 'ease-out' for a smoother finish
                    isAnimationActive={true} // Disable animation to improve performance
                    label={renderCustomizedLabel}
                  >
                    <Cell fill="rgb(95 112 228 / 1)" />
                    <Cell fill="rgb(95 210 228 / 1)" />
                  </Pie>
                  {/* <Tooltip
                    formatter={(value, name) => [value, name]}
                    contentStyle={{
                      backgroundColor: '#f5f5f5',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px'
                    }}
                  /> */}
                </PieChart>
              </ResponsiveContainer>

            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminDashboard;
